import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../utils/currency';
import Checkbox from '../../../ui/atoms/Checkbox';
import useCurrencyConfig from '../../../hooks/whitelabel/useCurrencyConfig';

/**
 * CheckoutWalletCheckbox component.
 * Render a checkbox to enable/disable wallet usage during checkout with balance information.
 *
 * @param {object} props - checkboxContext, setUsingWallet, loadingWallet, usingWallet, usableBalance, walletBalanceUsed, purchaseToken.
 * @param {Function} props.checkboxContext - Function to determine checkbox context based on loading and usage states.
 * @param {Function} props.setUsingWallet - Callback function to toggle wallet usage with purchase token and state.
 * @param {boolean} props.loadingWallet - Indicates if wallet information is currently being loaded.
 * @param {boolean} props.usingWallet - Current state of wallet usage (enabled/disabled).
 * @param {number} props.usableBalance - Available wallet balance that can be used for the purchase.
 * @param {number} props.walletBalanceUsed - Amount of wallet balance being used in the current purchase.
 * @param {string} props.purchaseToken - Unique token identifying the current purchase session.
 * @returns {JSX.Element} The rendered wallet checkbox component.
 */
const CheckoutWalletCheckbox = (props) => {
  const {
    checkboxContext,
    setUsingWallet,
    loadingWallet,
    usingWallet,
    usableBalance,
    walletBalanceUsed,
    purchaseToken,
  } = props;

  const { t } = useTranslation('loyalty');
  const { currency, locale } = useCurrencyConfig();

  return (
    <Checkbox
      name="wallet"
      isLoading={loadingWallet}
      checked={usingWallet}
      labelText={t('wallet_checkbox_label', {
        context: checkboxContext(loadingWallet, usingWallet),
        amount: formatCurrency(usableBalance, 2, currency, locale),
        using: formatCurrency(walletBalanceUsed, 2, currency, locale),
      })}
      id="wallet-checkbox"
      onChange={() => setUsingWallet(purchaseToken, !usingWallet, 25)}
    />
  );
};

CheckoutWalletCheckbox.propTypes = {
  checkboxContext: PropTypes.func.isRequired,
  setUsingWallet: PropTypes.func.isRequired,
  loadingWallet: PropTypes.bool.isRequired,
  usingWallet: PropTypes.bool.isRequired,
  usableBalance: PropTypes.number.isRequired,
  walletBalanceUsed: PropTypes.number.isRequired,
  purchaseToken: PropTypes.string.isRequired,
};

export default CheckoutWalletCheckbox;
